import {
  hamburgerButton,
  overlay,
  menuPrimary,
  menuParentItems,
  header,
  viewport,
  sidebar,
  notification,
  logo,
} from './elements.js';
import { changeElementHeight } from './lib.js';
import { arrayUnique } from './utils.js';

/**
 * Toggle main hamburger menu
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 */
export function toggleMenu(e) {
  const expanded =
    hamburgerButton.getAttribute('aria-expanded') === 'true' || false;

  if (
    e.currentTarget === hamburgerButton ||
    ((e.currentTarget === overlay || e.keyCode === 27) && expanded === true)
  ) {
    menuPrimary.classList.toggle('-is-visible');
    overlay.classList.toggle('-is-visible');
    hamburgerButton.setAttribute('aria-expanded', !expanded);

    if (expanded) {
      menuPrimary.style.height = `${0}px`;
      setTimeout(function() {
        header.classList.toggle('-is-visible');
        logo.classList.toggle('-is-hidden');
      }, 350);
    } else {
      menuPrimary.style.height = `${menuPrimary.scrollHeight}px`;
      header.classList.toggle('-is-visible');
      logo.classList.toggle('-is-hidden');
    }
  }
}

/**
 * Handle menu clicks to toggle submenus
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 */
export function handleMenuClick(e) {
  e.preventDefault();
  const submenuTransitionTiming = 120;
  const menuItem = e.target.closest('.c-menu__link');
  const expanded = menuItem.getAttribute('aria-expanded') === 'true' || false;
  const submenu = menuItem.nextElementSibling;
  if (expanded) {
    const menuHeightDiff = menuPrimary.scrollHeight - submenu.scrollHeight;
    changeElementHeight(menuPrimary, menuHeightDiff);
    changeElementHeight(submenu, 0);
    setTimeout(function() {
      menuItem.setAttribute('aria-expanded', !expanded);
    }, submenuTransitionTiming);
  } else {
    let itemSubmenuHeight = 0;
    menuParentItems.forEach(item => {
      if (item.getAttribute('aria-expanded') === 'true') {
        const itemSubmenu = item.nextElementSibling;
        itemSubmenuHeight = itemSubmenu.scrollHeight;
        changeElementHeight(itemSubmenu, 0);
        setTimeout(function() {
          item.setAttribute('aria-expanded', false);
        }, submenuTransitionTiming);
      }
    });
    menuItem.setAttribute('aria-expanded', !expanded);
    const menuHeightDiff =
      menuPrimary.scrollHeight + submenu.scrollHeight - itemSubmenuHeight;
    changeElementHeight(submenu, submenu.scrollHeight);
    changeElementHeight(menuPrimary, menuHeightDiff);
    if (menuItem.classList.contains('c-menu__link--search')) {
      setTimeout(function() {
        submenu.querySelector('input[type="search"]').focus();
      }, submenuTransitionTiming);
    }
  }
}

/**
 * Make sidebar sticky only if there is enough vertical space
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 */
export function makeSidebarSticky() {
  if (sidebar.clientHeight < viewport.clientHeight) {
    sidebar.classList.add('l-sidebar--sticky');
  } else {
    sidebar.classList.remove('l-sidebar--sticky');
  }
}

/**
 * Move global notice below menu on mobile.
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 */
export function moveNotification(e) {
  if (e.matches) {
    document.body.prepend(notification);
  } else {
    header.appendChild(notification);
  }
}

/**
 * Check if notification was previously dismissed
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 */
export function checkNotificationStatus() {
  const expirationStatus =
    notification.getAttribute('data-expiration') === 'true' || false;
  let expired = false;
  if (expirationStatus) {
    const expirationUnixTimestamp = parseInt(
      notification.getAttribute('data-expiration-timestamp')
    );
    const currentUnixTimestamp = Math.round(Date.now() / 1000); // Convert to ms

    if (currentUnixTimestamp > expirationUnixTimestamp) {
      expired = true;
    }
  }

  const removedNotifications =
    JSON.parse(localStorage.getItem('removedNotifications')) || [];

  const dismissedNotifications =
    JSON.parse(sessionStorage.getItem('dismissedNotifications')) || [];

  const notificationID = notification.id;

  if (
    !removedNotifications.includes(notificationID) &&
    !dismissedNotifications.includes(notificationID) &&
    !expired
  ) {
    notification.setAttribute('data-show-notification', 'true');
  }
}

/**
 * Dismiss notification and store data in localStore
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 */
export function dismissNotification(e) {
  const notificationID = e.target.dataset.dismiss;

  const currentNotification = document.querySelector(`#${notificationID}`);

  currentNotification.setAttribute('data-show-notification', 'false');

  if (currentNotification.classList.contains('c-notification--removable')) {
    const removedNotifications =
      JSON.parse(localStorage.getItem('removedNotifications')) || [];

    removedNotifications.push(notificationID);
    const removedNotificationsUnique = arrayUnique(removedNotifications);
    localStorage.setItem(
      'removedNotifications',
      JSON.stringify(removedNotificationsUnique)
    );
  } else {
    const dismissedNotifications =
      JSON.parse(sessionStorage.getItem('dismissedNotifications')) || [];

    dismissedNotifications.push(notificationID);
    const dismissedNotificationsUnique = arrayUnique(dismissedNotifications);
    sessionStorage.setItem(
      'dismissedNotifications',
      JSON.stringify(dismissedNotificationsUnique)
    );
  }
}

/**
 * Handle card click to make the whole element clickable allowing inside links
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 */
export function handleCardClick(e) {
  const isTextSelected = window.getSelection().toString();
  const cardLink = e.target.closest('.js-card').querySelector('.js-main-link');
  const clickableElements = e.target.closest('.js-card').querySelectorAll('a');
  clickableElements.forEach(el =>
    el.addEventListener('click', ev => ev.stopPropagation())
  );
  if (!isTextSelected) {
    cardLink.click();
  }
}

/**
 * Handle calendar checkbox to toggle calendar visibility
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 */
export function applyCalendarSettings(e) {
  document.body.classList.toggle(`hide-${e.target.value}`);
}
